import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  RefreshCw,
  FileX,
  AlertCircle,
  FolderOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { DocumentListItem } from "./document-list-item";
import { DocumentUploadZone } from "./document-upload-zone";
import { useDocumentApi } from "@/providers/DocumentApiProvider";
import type { PurchaseRequest } from "@/types/purchase-request";
import type { PurchaseRequestDocument } from "@/services/adm-document-service";
import {
  fetchAdmDocumentsForPurchaseRequest,
  uploadAdmDocumentForPurchaseRequest,
  downloadAdmDocument,
  deleteAdmDocument,
  previewAdmDocument,
} from "@/services/adm-document-service";

interface PurchaseRequestDocumentsTabProps {
  purchaseRequest: PurchaseRequest;
  onDocumentCountChange?: (count: number) => void;
}

export function PurchaseRequestDocumentsTab({
  purchaseRequest,
  onDocumentCountChange,
}: PurchaseRequestDocumentsTabProps) {
  const { getDocumentContext, getAccessToken } = useDocumentApi();

  const [documents, setDocuments] = useState<PurchaseRequestDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // TODO: UI-PTX-012 — confirm which key to use for PR document lookups.
  const lookupKey =
    purchaseRequest.requestId ||
    (purchaseRequest.finOpsPrimaryKey != null ? String(purchaseRequest.finOpsPrimaryKey) : undefined) ||
    purchaseRequest.poNumber?.[0] ||
    "";

  const loadDocuments = useCallback(async () => {
    if (!getAccessToken || !lookupKey) return;

    setIsLoading(true);
    setIsError(false);
    setNotConfigured(false);

    try {
      const ctx = await getDocumentContext();
      if (!ctx) {
        setNotConfigured(true);
        setDocuments([]);
        return;
      }

      const docs = await fetchAdmDocumentsForPurchaseRequest(
        ctx,
        getAccessToken,
        lookupKey,
      );
      setDocuments(docs);
      onDocumentCountChange?.(docs.length);
    } catch (err) {
      console.error("[ADM] Failed to load documents:", err);
      setIsError(true);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [getDocumentContext, getAccessToken, lookupKey, onDocumentCountChange]);

  useEffect(() => {
    if (!hasFetched) {
      loadDocuments();
    }
  }, [hasFetched, loadDocuments]);

  // Client-side search filter
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter((doc) => doc.fileName.toLowerCase().includes(q));
  }, [documents, searchQuery]);

  // Selection helpers
  const allVisibleSelected =
    filteredDocuments.length > 0 &&
    filteredDocuments.every((d) => selectedIds.has(d.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredDocuments.map((d) => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // Upload
  const handleUpload = async (file: File) => {
    if (!getAccessToken) return;

    setIsUploading(true);
    try {
      const ctx = await getDocumentContext();
      if (!ctx) {
        toast.error("Document Management is not configured for this company");
        return;
      }
      await uploadAdmDocumentForPurchaseRequest(ctx, getAccessToken, file, {
        requestId: lookupKey,
        description: purchaseRequest.description,
      });
      toast.success("Document uploaded successfully");
      await loadDocuments();
    } catch (err) {
      console.error("[ADM] Upload failed:", err);
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  // Preview
  const handlePreview = async (document: PurchaseRequestDocument) => {
    if (!getAccessToken) return;
    try {
      const ctx = await getDocumentContext();
      if (!ctx) return;
      await previewAdmDocument(ctx, getAccessToken, document.id);
    } catch (err) {
      console.error("[ADM] Preview failed:", err);
      toast.error("Failed to preview document");
    }
  };

  // Download
  const handleDownload = async (document: PurchaseRequestDocument) => {
    if (!getAccessToken) return;
    try {
      const ctx = await getDocumentContext();
      if (!ctx) return;
      await downloadAdmDocument(ctx, getAccessToken, document.id, document.fileName);
    } catch (err) {
      console.error("[ADM] Download failed:", err);
      toast.error("Failed to download document");
    }
  };

  // Delete
  const handleDelete = async (document: PurchaseRequestDocument) => {
    if (!getAccessToken) return;

    setDeletingId(document.id);
    try {
      const ctx = await getDocumentContext();
      if (!ctx) return;
      await deleteAdmDocument(ctx, getAccessToken, document.id);
      toast.success("Document deleted");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(document.id);
        return next;
      });
      await loadDocuments();
    } catch (err) {
      console.error("[ADM] Delete failed:", err);
      toast.error("Failed to delete document");
    } finally {
      setDeletingId(null);
    }
  };

  // Refresh
  const handleRefresh = () => {
    setSearchQuery("");
    setHasFetched(false);
  };

  const selectedCount = selectedIds.size;

  // Not configured fallback
  if (notConfigured) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
          <FolderOpen className="h-7 w-7 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium">Document Management not configured</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            This company does not have Access Document Management configured.
            Documents will be available once ADM is set up for this instance.
          </p>
        </div>
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-xs text-amber-800 dark:text-amber-300 max-w-sm text-left">
          <p className="font-semibold flex items-center gap-1.5 mb-1">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            Engineering — API integration required
          </p>
          <p>
            Integrate the Document Management API or replace this tab with
            the Documents MFE when available. See <strong>Engineering Notes UI-PTX-012</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* Upload zone */}
      <DocumentUploadZone onUpload={handleUpload} isUploading={isUploading} />

      {/* Search bar + refresh */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing Document Name"
            className="pl-8 h-8 text-sm"
            aria-label="Search documents"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0"
          onClick={handleRefresh}
          disabled={isLoading}
          aria-label="Refresh documents"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Select all + count */}
      {!isLoading && !isError && filteredDocuments.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="pr-select-all"
              checked={allVisibleSelected}
              onCheckedChange={(v) => handleSelectAll(v === true)}
              aria-label="Select all documents"
            />
            <label
              htmlFor="pr-select-all"
              className="text-xs font-medium cursor-pointer select-none"
            >
              Select All
            </label>
            <span className="text-xs text-muted-foreground">
              — {selectedCount} of {filteredDocuments.length} selected
            </span>
          </div>
        </div>
      )}

      {/* Document list */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border p-3">
                <Skeleton className="h-4 w-4 mt-1 rounded flex-shrink-0" />
                <Skeleton className="h-9 w-9 rounded-md flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <AlertCircle className="h-10 w-10 text-destructive/60" />
            <div>
              <p className="text-sm font-medium">Failed to load documents</p>
              <p className="text-xs text-muted-foreground mt-1">
                There was a problem retrieving documents for this purchase request.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Try Again
            </Button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <FileX className="h-10 w-10 text-muted-foreground/50" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {searchQuery
                  ? "No documents match your search"
                  : "No documents found for this purchase request"}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-xs text-primary hover:underline mt-1"
                >
                  Clear search
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <DocumentListItem
              key={doc.id}
              document={doc}
              isSelected={selectedIds.has(doc.id)}
              onSelectChange={handleSelectChange}
              onPreview={handlePreview}
              onDownload={handleDownload}
              onDelete={handleDelete}
              isDeleting={deletingId === doc.id}
            />
          ))
        )}
      </div>

      {/* Bulk action toolbar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2 text-xs font-medium gap-2 flex-shrink-0">
          <span className="text-muted-foreground">
            {selectedCount} document{selectedCount > 1 ? "s" : ""} selected
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              for (const id of selectedIds) {
                const doc = documents.find((d) => d.id === id);
                if (doc) handleDownload(doc);
              }
            }}
          >
            Download
          </Button>
        </div>
      )}
    </div>
  );
}
